import {
  Box,
  ButtonProps,
  chakra,
  Circle,
  Divider,
  Flex,
  Grid,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SystemStyleObject,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
  UseDisclosureProps,
  useFormControl,
} from "@chakra-ui/react";
import { RemixiconComponentType, RiCheckLine } from "@remixicon/react";
import {
  forwardRef,
  memo,
  MouseEvent,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ColorPicker } from "@/common/components/ColorPicker";
import {
  CustomIconEmojiDisplay,
  IcoAlert,
  IconValueKey,
  useDefaultIconColor,
  useResolveIconColor,
} from "@/common/components/Icons";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import SearchInput from "@/common/components/SearchInput";
import { CustomIconMap } from "@/common/utils/customIcons";
import { EmojiKey, EmojiMap, isEmoji } from "@reflag/shared/emojis";
import { CustomIconKey } from "@reflag/shared/icons";
import { filterMap } from "@reflag/shared/utils/array";

const CheckIcon = chakra(RiCheckLine);

export interface IconPickerValue {
  icon: IconValueKey | null;
  color: string | null;
}

const presetColors = [
  "#95a2b3", // gray
  "#5e6ad2", // purple
  "#26b5ce", // teal
  "#4cb782", // green
  "#f2c94c", // yellow
  "#f2994a", // orange
  "#f7c8c1", // pink
  "#eb5757", // red
];

type FlagListItem = [CustomIconKey, RemixiconComponentType];
type EmojiListItem = [EmojiKey, string];

/**
 * IconListItem is a high performance memoized component for large lists of icons or emojis.
 * This component does not use Chakra UI components, hooks, or event handlers.
 */
const IconListItem = memo(function IconListItem({
  iconKey,
  icon,
  type,
  isSelected,
}: {
  iconKey: IconValueKey | null;
  icon: ReactNode;
  type: "icon" | "emoji";
  isSelected: boolean;
}) {
  return (
    <div
      aria-selected={isSelected}
      className="icon-list-item"
      data-key={iconKey}
      data-type={type}
      title={iconKey ?? "Default"}
    >
      {icon}
    </div>
  );
});

export type IconPickerProps = Omit<ButtonProps, "value" | "onChange"> & {
  value?: IconPickerValue;
  size?: "xs" | "sm" | "md" | "lg";
  isDisabled?: boolean;
  placeholder?: string;
  onChange?: (value: IconPickerValue) => void;
  rightAction?:
    | ((
        contentRef: RefObject<HTMLDivElement>,
        disclosure: UseDisclosureProps,
      ) => ReactNode)
    | ReactNode;
};

export const IconPicker = forwardRef<HTMLButtonElement, IconPickerProps>(
  function IconPicker(
    {
      value = { icon: null, color: null },
      size = "sm",
      isDisabled = false,
      placeholder = "Select icon",
      rightAction,
      onChange,
      ...rest
    },
    ref,
  ) {
    const disclosure = useDisclosure();
    const contentRef = useRef<HTMLDivElement>(null);
    const formControlProps = useFormControl<HTMLButtonElement>(rest);

    const { color, isAdjusted: isAdjustedColor } = useResolveIconColor(
      value.color,
    );

    const iconListItemStyles = useMemo(() => {
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "md",
        boxSize: 8,
        color: color,
        cursor: "pointer",
        fontSize: "16px",
        "&:hover": {
          backgroundColor: "gray.50",
          _dark: {
            backgroundColor: "gray.700",
          },
        },
        "&[aria-selected=true][data-type=icon]": {
          color: "white",
          backgroundColor: color,
          _dark: {
            backgroundColor: color,
            color: "black",
          },
        },
        "&[aria-selected=true][data-type=emoji]": {
          backgroundColor: "brand.200",
          _dark: {
            backgroundColor: "brand.800",
          },
        },
      } satisfies SystemStyleObject;
    }, [color]);

    // Scroll to selected icon/emoji when popover opens
    useEffect(() => {
      if (disclosure.isOpen && contentRef.current) {
        const selectedElement = contentRef.current.querySelector(
          "[aria-selected=true]",
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "auto",
            block: "center",
            inline: "center",
          });
        }
      }
    }, [disclosure.isOpen]);

    const handleIconSelect = (e: MouseEvent<HTMLDivElement>) => {
      const icon = (e.target as HTMLElement).closest<HTMLElement>("[data-key]")
        ?.dataset.key as IconValueKey | null;
      onChange?.({
        ...value,
        icon: icon !== "Default" ? icon : null,
      });
      disclosure.onClose();
    };

    const handleColorSelect = (color: string | null) => {
      onChange?.({ ...value, color });
    };

    return (
      <Popover
        {...disclosure}
        closeOnBlur={true}
        placement="bottom-start"
        isLazy
      >
        <PopoverTrigger>
          <IconButton
            {...formControlProps}
            ref={ref}
            aria-label={placeholder}
            icon={
              <CustomIconEmojiDisplay
                color={value.color}
                iconKey={value.icon}
                size={size}
              />
            }
            isDisabled={isDisabled}
            size={size}
            variant="outline"
            {...rest}
          />
        </PopoverTrigger>

        <PopoverContent ref={contentRef} w="sm">
          <PopoverArrow />

          <PopoverBody p={0}>
            <Tabs
              defaultIndex={isEmoji(value.icon) ? 1 : 0}
              display="flex"
              flexDirection="column"
              variant="chip"
              isLazy
            >
              <TabList>
                <Tab>
                  <Box className="innerTab">Icons</Box>
                </Tab>
                <Tab>
                  <Box className="innerTab">Emojis</Box>
                </Tab>
                {rightAction && (
                  <Flex alignItems="center" ml="auto">
                    {typeof rightAction === "function"
                      ? rightAction(contentRef, disclosure)
                      : rightAction}
                  </Flex>
                )}
              </TabList>

              <TabPanels
                sx={{
                  ".icon-list-item": iconListItemStyles,
                }}
              >
                <IconTab
                  isAdjustedColor={isAdjustedColor}
                  value={value}
                  onColorSelect={handleColorSelect}
                  onIconSelect={handleIconSelect}
                />
                <EmojiTab value={value} onEmojiSelect={handleIconSelect} />
              </TabPanels>
            </Tabs>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  },
);

const sharedCircleStyles = {
  _hover: { transform: "scale(1.1)" },
  cursor: "pointer",
  size: 5,
  transition: "transform 0.2s",
};

type IconTabProps = {
  value: IconPickerValue;
  isAdjustedColor: boolean;
  onIconSelect: (e: MouseEvent<HTMLDivElement>) => void;
  onColorSelect: (color: string | null) => void;
};

function IconTab({
  value,
  isAdjustedColor,
  onIconSelect,
  onColorSelect,
}: IconTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const defaultGray = useDefaultIconColor();

  const filteredIcons = useMemo(() => {
    const trimmedSearchQuery = searchQuery.trim();
    const icons = Object.entries(CustomIconMap).filter(
      ([_, icon]) => icon,
    ) as FlagListItem[];
    if (!trimmedSearchQuery) return icons;
    return icons.filter(([iconName]) =>
      iconName.toLowerCase().includes(trimmedSearchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const checkColor = useColorModeValue("white", "gray.900");
  const adjustedWarningColor = useColorModeValue("orange.500", "yellow.600");

  return (
    <TabPanel p={0}>
      <Grid
        gap={2}
        justifyItems="center"
        p={4}
        position="relative"
        templateColumns="repeat(9, 1fr) auto 1fr"
      >
        <Circle
          {...sharedCircleStyles}
          bg={defaultGray}
          onClick={() => onColorSelect(null)}
        >
          {!value.color && <CheckIcon color={checkColor} size={14} />}
        </Circle>
        {presetColors.map((presetColor) => (
          <Circle
            {...sharedCircleStyles}
            key={presetColor}
            bg={presetColor}
            onClick={() => onColorSelect(presetColor)}
          >
            {value.color?.toLowerCase() === presetColor.toLowerCase() && (
              <CheckIcon color={checkColor} size={14} />
            )}
          </Circle>
        ))}
        <Divider borderColor="chakra-border-color" orientation="vertical" />
        <ColorPicker
          {...sharedCircleStyles}
          aria-label="Custom color picker"
          value={value.color ?? undefined}
          onChange={onColorSelect}
        />
        {isAdjustedColor && (
          <InfoIconTooltip
            _icon={{ position: "absolute", right: 2, top: 2 }}
            icon={<IcoAlert color={adjustedWarningColor} size="sm" />}
            text="The color has been adjusted to ensure sufficient contrast with the background."
          />
        )}
      </Grid>

      <Box mb={1} mx={2}>
        <SearchInput
          expandable={false}
          placeholder="Search icons..."
          value={searchQuery}
          variant="filled"
          w="full"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      <Box maxH="sm" overflowY="auto" p={2}>
        <Grid
          gap={0.5}
          templateColumns="repeat(10, 1fr)"
          onClick={onIconSelect}
        >
          {filteredIcons.map(([iconKey, Icon], index) => (
            <IconListItem
              key={iconKey}
              icon={<Icon size={16} />}
              iconKey={iconKey}
              isSelected={
                (!value.icon && index === 0) || value.icon === iconKey
              }
              type="icon"
            />
          ))}
        </Grid>

        {filteredIcons.length === 0 && (
          <Box color="gray.500" fontSize="sm" py={4} textAlign="center">
            No icons found
          </Box>
        )}
      </Box>
    </TabPanel>
  );
}

type EmojiTabProps = {
  value: IconPickerValue;
  onEmojiSelect: (e: MouseEvent<HTMLDivElement>) => void;
};

function EmojiTab({ value, onEmojiSelect }: EmojiTabProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEmojis = useMemo(() => {
    const trimmedSearchQuery = searchQuery.trim();
    const emojis = EmojiMap.map(({ name, emojis }) => ({
      name,
      emojis: Object.entries(emojis) as EmojiListItem[],
    }));
    if (!trimmedSearchQuery) return emojis;
    return filterMap(emojis, ({ name, emojis }) => {
      const filteredEmojis = emojis.filter(([emojiKey]) => {
        return emojiKey
          .toLowerCase()
          .includes(trimmedSearchQuery.toLowerCase());
      });
      if (filteredEmojis.length) {
        return {
          name,
          emojis: filteredEmojis,
        };
      }
    });
  }, [searchQuery]);

  return (
    <TabPanel p={0}>
      <Box mb={1} mt={2} mx={2}>
        <SearchInput
          expandable={false}
          placeholder="Search emojis..."
          value={searchQuery}
          variant="filled"
          w="full"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      <Box maxH="sm" overflowY="auto" p={2}>
        {filteredEmojis.map(({ name, emojis }) => (
          <Box key={name} mb={4}>
            <Text color="dimmed" fontSize="sm" mb={2}>
              {name}
            </Text>
            <Grid
              gap={0.5}
              templateColumns="repeat(10, 1fr)"
              onClick={onEmojiSelect}
            >
              {emojis.map(([emojiKey, emoji]) => (
                <IconListItem
                  key={emojiKey}
                  icon={emoji}
                  iconKey={`:${emojiKey}:`}
                  isSelected={value.icon === `:${emojiKey}:`}
                  type="emoji"
                />
              ))}
            </Grid>
          </Box>
        ))}

        {Object.keys(filteredEmojis).length === 0 && (
          <Box color="gray.500" fontSize="sm" py={4} textAlign="center">
            No emojis found
          </Box>
        )}
      </Box>
    </TabPanel>
  );
}
